import { useEffect, useState, Dispatch, SetStateAction } from 'react';
import {
  Col,
  Form,
  Row,
  InputGroup,
  ToggleButtonGroup,
  ToggleButton,
  Button,
} from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import Select from 'react-select';
import { BiSearch } from 'react-icons/bi';
import {
  DatePickerInput,
  DatesProvider,
  DatesRangeValue,
  DateValue,
} from '@mantine/dates';
import ModalMessage from '@components/elements/ModalMessage/ModalMessage';
import {
  IJobListFilters,
  IJobSearchType,
  IJobSearchScope,
  JobListFiltersKeys,
  JobListViews,
  JobShiftsQuickFilters,
  IJobSearchPeriod,
} from '@th-types/jobs.type';
import DATE_FORMATS from '@constants/dateFormat';
import {
  companyHiddenJobs,
  companyJobs,
  companyPastJobs,
  jobListQuickFilterActions,
  JobListQuickFiltersMobile,
  jobShiftsQuickFilterActions,
  JobShiftsQuickFilterOption,
  memberHiddenJobs,
  memberJobs,
  memberPastJobs,
} from '@company/types/job-list.type';
import './style.css';

const SHOW_JOB_SHIFTS_VIEW = import.meta.env.VITE_SHOW_JOB_SHIFTS;

interface Props {
  countHiddenJobs: number;
  jobListFilters: IJobListFilters;
  searchJobs: () => void;
  setCurrentOffset: (currentOffset: number) => void;
  setJobListFilters: Dispatch<SetStateAction<IJobListFilters>>;
  setJobListViewType: (jobListViewType: JobListViews) => void;
}

export default function JobListManagerHeader({
  jobListFilters,
  countHiddenJobs,
  searchJobs,
  setCurrentOffset,
  setJobListFilters,
  setJobListViewType,
}: Props) {
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [selectedJobListQuickFilter, setSelectedJobListQuickFilter] =
    useState(memberJobs);
  const [
    selectedJobShiftsListQuickFilter,
    setSelectedJobShiftsListQuickFilter,
  ] = useState<JobShiftsQuickFilterOption | undefined>();

  const [pickedDateRange, setPickedDateRange] = useState<
    DatesRangeValue<DateValue> | undefined
  >();

  const [searchValue, setSearchValue] = useState(
    jobListFilters.searchFilter || ''
  );

  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (pickedDateRange) {
      const startDate = pickedDateRange[0] as string;
      const endDate = pickedDateRange[1] as string;
      setJobListFilters((previousFilters) => ({
        ...previousFilters,
        dateRange: {
          startDate: pickedDateRange[0]
            ? new Date(pickedDateRange[0])
            : undefined,
          endDate: pickedDateRange[1]
            ? new Date(pickedDateRange[1])
            : undefined,
        },
      }));
      searchParams.set(JobListFiltersKeys.DATE_FROM, startDate ?? '');
      searchParams.set(JobListFiltersKeys.DATE_TO, endDate ?? '');
      setSearchParams(searchParams);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickedDateRange]);

  useEffect(() => {
    if (countHiddenJobs === 0) {
      if (selectedJobListQuickFilter === memberHiddenJobs) {
        setSelectedJobListQuickFilter(memberJobs);
      } else if (selectedJobListQuickFilter === companyHiddenJobs) {
        setSelectedJobListQuickFilter(companyJobs);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countHiddenJobs]);

  const handleTextSearch = (value: string) => {
    setSearchValue(value);
    setJobListFilters((previousFilters) => ({
      ...previousFilters,
      searchFilter: value,
    }));
  };

  const fetchCompanyJobs = async (fetchHidden: boolean, fetchPast: boolean) => {
    setCurrentOffset(0);
    const jobSearchType = fetchPast
      ? IJobSearchType.PAST_COMPANY
      : IJobSearchType.ACTIVE_COMPANY;
    setJobListFilters((previousFilters) => ({
      ...previousFilters,
      scope: IJobSearchScope.COMPANY,
      period: fetchPast ? IJobSearchPeriod.PAST : IJobSearchPeriod.ACTIVE,
      hiddenJobs: fetchHidden,
      jobSearchType,
    }));
  };

  const fetchMemberJobs = async (fetchHidden: boolean, fetchPast: boolean) => {
    setCurrentOffset(0);
    const jobSearchType = fetchPast
      ? IJobSearchType.PAST_MEMBER
      : IJobSearchType.ACTIVE_MEMBER;

    setJobListFilters((previousFilters) => ({
      ...previousFilters,
      scope: IJobSearchScope.MEMBER,
      hiddenJobs: fetchHidden,
      period: fetchPast ? IJobSearchPeriod.PAST : IJobSearchPeriod.ACTIVE,
      jobSearchType,
    }));
  };

  const clearFilters = () => {
    setJobListFilters((previousFilters) => ({
      ...previousFilters,
      searchFilter: '',
      dateRange: {
        startDate: undefined,
        endDate: undefined,
      },
    }));
    setSelectedJobShiftsListQuickFilter(undefined);
    setPickedDateRange(['', '']);
    setSearchValue('');
    searchParams.set(JobListFiltersKeys.DATE_FROM, '');
    searchParams.set(JobListFiltersKeys.DATE_TO, '');
    searchParams.set(JobListFiltersKeys.SEARCH_TEXT, '');
    setSearchParams(searchParams);
  };

  const handleQuickFilterSelection = (value: JobListQuickFiltersMobile) => {
    switch (value) {
      case JobListQuickFiltersMobile.MEMBER_JOBS:
        setSelectedJobListQuickFilter(memberJobs);
        fetchMemberJobs(false, false);
        break;
      case JobListQuickFiltersMobile.MEMBER_HIDDEN_JOBS:
        setSelectedJobListQuickFilter(memberHiddenJobs);
        fetchMemberJobs(true, false);
        break;
      case JobListQuickFiltersMobile.MEMBER_PAST_JOBS:
        setSelectedJobListQuickFilter(memberPastJobs);
        fetchMemberJobs(false, true);
        break;
      case JobListQuickFiltersMobile.COMPANY_JOBS:
        setSelectedJobListQuickFilter(companyJobs);
        fetchCompanyJobs(false, false);
        break;
      case JobListQuickFiltersMobile.COMPANY_HIDDEN_JOBS:
        setSelectedJobListQuickFilter(companyHiddenJobs);
        fetchCompanyJobs(true, false);
        break;
      case JobListQuickFiltersMobile.COMPANY_PAST_JOBS:
        setSelectedJobListQuickFilter(companyPastJobs);
        fetchCompanyJobs(false, true);
        break;
      default:
        break;
    }
  };

  const handleJobShiftsQuickFilterSelection = (
    option?: JobShiftsQuickFilterOption
  ) => {
    setSelectedJobShiftsListQuickFilter(option);
    setJobListFilters((previousFilters) => ({
      ...previousFilters,
      toApprove: option?.value === JobShiftsQuickFilters.TO_APPROVE,
      markPaid: option?.value === JobShiftsQuickFilters.MARK_PAID,
      missingHours: option?.value === JobShiftsQuickFilters.MISSING_HOURS,
      noCheckInOut: option?.value === JobShiftsQuickFilters.NO_CHECK_IN_OUT,
      onboarding: option?.value === JobShiftsQuickFilters.ONBOARDING,
      openShifts: option?.value === JobShiftsQuickFilters.CURRENT_OPEN_SHIFTS,
    }));
  };

  const getJobListQuickFilters = () => {
    return jobListQuickFilterActions.filter(
      (item) => item.value !== selectedJobListQuickFilter.value
    );
  };

  const getJobShiftsListQuickFilters = (): JobShiftsQuickFilterOption[] => {
    return jobShiftsQuickFilterActions.filter(
      (item) => item.value !== selectedJobShiftsListQuickFilter?.value
    );
  };

  const changeViewType = (viewType: JobListViews) => {
    if (SHOW_JOB_SHIFTS_VIEW === 'true') {
      setJobListViewType(viewType);
    } else {
      setShowMessageModal(true);
    }
  };

  return (
    <>
      <Row className="mx-0 my-2 px-2 g-0">
        <strong>Job Manager</strong>
      </Row>
      <Row className="mx-0 my-2 g-0">
        <ToggleButtonGroup
          type="radio"
          name="options"
          value={1}
          className="rounded"
        >
          <ToggleButton
            value={JobListViews.JOBS}
            id={JobListViews.JOBS}
            onClick={() => changeViewType(JobListViews.JOBS)}
            variant={`${jobListFilters.viewType === JobListViews.JOBS ? 'primary' : 'outline-dark'}`}
            className="border"
          >
            <strong>{JobListViews.JOBS}</strong>
          </ToggleButton>
          <ToggleButton
            value={JobListViews.SHIFTS}
            id={JobListViews.SHIFTS}
            onClick={() => changeViewType(JobListViews.SHIFTS)}
            variant={`${jobListFilters.viewType === JobListViews.SHIFTS ? 'primary' : 'outline-dark'}`}
            className="border"
          >
            <strong>{JobListViews.SHIFTS}</strong>
          </ToggleButton>
        </ToggleButtonGroup>
      </Row>
      <Row className="mx-0 my-2 g-0">
        <InputGroup>
          <InputGroup.Text className="search-icon">
            <BiSearch size={20} className="mt-1" />
          </InputGroup.Text>
          <Form.Control
            placeholder="By event name, brand or city"
            className="form-control-header"
            value={searchValue}
            onChange={(e) => handleTextSearch(e.target.value)}
            onKeyUp={(event) => {
              if (event.key === 'Enter') {
                searchJobs();
              }
            }}
          />
        </InputGroup>
      </Row>
      <Row className="mx-0 my-1">
        <Col xs={6}>
          <span className="text-uppercase fw-bold">DATE RANGE</span>
          <DatesProvider settings={{ consistentWeeks: false }}>
            <DatePickerInput
              type="range"
              aria-label="Date range picker"
              data-testid="date-range-picker"
              dropdownType="modal"
              modalProps={{
                centered: true,
              }}
              valueFormat={DATE_FORMATS.DATEPICKER_SHORT_FORMAT}
              placeholder="Select Range"
              radius="sm"
              size="sm"
              value={pickedDateRange}
              onChange={setPickedDateRange}
            />
          </DatesProvider>
        </Col>
        <Col xs={6}>
          <span className="text-uppercase fw-bold">QUICK FILTERS</span>
          {jobListFilters.viewType === JobListViews.JOBS && (
            <Select
              options={getJobListQuickFilters()}
              value={selectedJobListQuickFilter}
              placeholder="Select filters"
              isSearchable={false}
              onChange={(val) => {
                if (val?.value) {
                  handleQuickFilterSelection(val?.value);
                }
              }}
              styles={{
                dropdownIndicator: (provided) => ({
                  ...provided,
                  color: 'var(--black)',
                }),
                indicatorSeparator: (provided) => ({
                  ...provided,
                  display: 'none',
                }),
                control: (provided) => ({
                  ...provided,
                  background: 'var(--light)',
                }),
              }}
              data-testid="quick-filter-select"
            />
          )}
          {jobListFilters.viewType === JobListViews.SHIFTS && (
            <Select
              options={getJobShiftsListQuickFilters()}
              defaultValue={selectedJobShiftsListQuickFilter}
              value={selectedJobShiftsListQuickFilter}
              placeholder="Select filters"
              isSearchable={false}
              isClearable
              onChange={(val) => {
                handleJobShiftsQuickFilterSelection(val || undefined);
              }}
              styles={{
                dropdownIndicator: (provided) => ({
                  ...provided,
                  color: 'var(--black)',
                }),
                indicatorSeparator: (provided) => ({
                  ...provided,
                  display: 'none',
                }),
                control: (provided) => ({
                  ...provided,
                  background: 'var(--light)',
                }),
              }}
              data-testid="quick-filter-select"
            />
          )}
        </Col>
      </Row>
      <Row className="mx-0 my-3">
        <Col xs={6}>
          <Button
            variant="primary"
            onClick={searchJobs}
            className="btn-font-size w-100"
          >
            <strong>SEARCH</strong>
          </Button>
        </Col>
        <Col xs={6}>
          <Button
            variant="light"
            onClick={clearFilters}
            className="btn-font-size w-100"
          >
            <strong>CLEAR</strong>
          </Button>
        </Col>
      </Row>

      {showMessageModal && (
        <ModalMessage
          message="This is exciting. We're coding your ability to track all your shifts in a 10,000 foot view, right from a single page"
          setShowModal={setShowMessageModal}
        />
      )}
    </>
  );
}
